// ===================== TYPES =====================

type StoreName =
  | 'patients'
  | 'patients_temporaires'
  | 'rendez_vous'
  | 'consultations'
  | 'prescriptions'
  | 'resultats_analyses'
  | 'medecins'
  | 'secretaires'
  | 'laborantins'
  | 'sage_femmes'
  | 'infirmiers'
  | 'comptables'
  | 'medicaments'
  | 'actualites'
  | 'dossiers_analyses'
  | 'suivis_patients'
  | 'fiches_surveillance'
  | 'fiches_soins'
  | 'bons_laboratoire'
  | 'disponibilites_medecins'
  | 'dossiers_roses'
  | 'file_attente'
  | 'assurances'
  | 'patient_assurances'
  | 'notifications'
  | 'messages';

const DB_NAME = 'clinique-sainte-famille-offline';
const DB_VERSION = 2;

// Tous les stores du projet — alignés avec syncQueue.ts
const ALL_STORES: StoreName[] = [
  'patients',
  'patients_temporaires',
  'rendez_vous',
  'consultations',
  'prescriptions',
  'resultats_analyses',
  'medecins',
  'secretaires',
  'laborantins',
  'sage_femmes',
  'infirmiers',
  'comptables',
  'medicaments',
  'actualites',
  'dossiers_analyses',
  'suivis_patients',
  'fiches_surveillance',
  'fiches_soins',
  'bons_laboratoire',
  'disponibilites_medecins',
  'dossiers_roses',
  'file_attente',
  'assurances',
  'patient_assurances',
  'notifications',
  'messages',
];

// Métadonnées de fraîcheur stockées par store
interface StoreMetadata {
  lastSyncedAt: number;    // timestamp de la dernière synchro complète du store
  recordCount: number;     // nombre d'enregistrements
  syncedOnline: boolean;   // true si la dernière synchro était online
}

// ===================== OUVERTURE AVEC MIGRATION =====================

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;

      // Créer les stores manquants (v1 → v2 ou fresh install)
      for (const name of ALL_STORES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      }

      // Store de métadonnées (fraîcheur)
      if (!db.objectStoreNames.contains('_metadata')) {
        db.createObjectStore('_metadata', { keyPath: 'storeName' });
      }

      // Migration v1 → v2 : ajout du champ _cachedAt pour tous les stores existants
      if (oldVersion < 2 && oldVersion > 0) {
        // Les stores existants gardent leurs données, le champ _cachedAt
        // sera ajouté automatiquement aux nouveaux enregistrements.
        // Pour les anciens enregistrements sans _cachedAt, on les traitera
        // comme "fraîcheur inconnue" côté lecture.
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ===================== MÉTADONNÉES DE FRAÎCHEUR =====================

async function getStoreMetadata(storeName: StoreName): Promise<StoreMetadata | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('_metadata', 'readonly');
      const store = tx.objectStore('_metadata');
      const request = store.get(storeName);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

async function updateStoreMetadata(storeName: StoreName, partial: Partial<StoreMetadata>): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('_metadata', 'readwrite');
      const store = tx.objectStore('_metadata');
      const getReq = store.get(storeName);

      getReq.onsuccess = () => {
        const existing = getReq.result || { storeName, lastSyncedAt: 0, recordCount: 0, syncedOnline: false };
        const updated = { ...existing, ...partial, storeName };
        store.put(updated);
      };
      getReq.onerror = () => reject(getReq.error);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silencieux
  }
}

/**
 * Marque un store comme synchronisé (vient d'être rempli depuis le réseau).
 */
async function markStoreSynced(storeName: StoreName, recordCount: number): Promise<void> {
  await updateStoreMetadata(storeName, {
    lastSyncedAt: Date.now(),
    recordCount,
    syncedOnline: true,
  });
}

/**
 * Vérifie si les données d'un store sont fraîches (moins de maxAgeMs).
 * Retourne la date de dernière synchro, ou null si inconnue.
 */
export async function getStoreFreshness(storeName: StoreName): Promise<{
  lastSyncedAt: number | null;
  ageMs: number | null;
  isStale: boolean;
  recordCount: number;
} | null> {
  const meta = await getStoreMetadata(storeName);
  if (!meta || meta.lastSyncedAt === 0) return null;
  const ageMs = Date.now() - meta.lastSyncedAt;
  return {
    lastSyncedAt: meta.lastSyncedAt,
    ageMs,
    isStale: ageMs > 30 * 60 * 1000, // 30 minutes = stale pour usage médical
    recordCount: meta.recordCount,
  };
}

/**
 * Formate la fraîcheur pour affichage utilisateur.
 * Ex: "Données du 12 août 2026 à 14:30 (hors-ligne)"
 */
export function formatFreshness(freshness: {
  lastSyncedAt: number | null;
  ageMs: number | null;
  isStale: boolean;
} | null, isOffline: boolean): string | null {
  if (!freshness || !freshness.lastSyncedAt) return null;
  const date = new Date(freshness.lastSyncedAt);
  const dateStr = date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const freshnessLabel = isOffline ? ' (hors-ligne)' : '';
  const staleLabel = freshness.isStale ? ' ⚠' : '';
  return `Données du ${dateStr} à ${timeStr}${freshnessLabel}${staleLabel}`;
}

// ===================== QUOTA MANAGEMENT =====================

let quotaWarningShown = false;

async function checkQuota(): Promise<boolean> {
  if (!('storage' in navigator) || !navigator.storage?.estimate) return true;
  try {
    const estimate = await navigator.storage.estimate();
    if (!estimate.quota || !estimate.usage) return true;
    const usageRatio = estimate.usage / estimate.quota;
    // Alerter si on dépasse 85% du quota
    if (usageRatio > 0.85) {
      if (!quotaWarningShown) {
        quotaWarningShown = true;
        // Dispatch un événement personnalisé pour que l'UI puisse réagir
        window.dispatchEvent(new CustomEvent('offline-storage-quota-warning', {
          detail: {
            usage: estimate.usage,
            quota: estimate.quota,
            usagePercent: Math.round(usageRatio * 100),
          },
        }));
      }
      if (usageRatio > 0.95) {
        console.error('[OfflineDB] Quota dépassé — stockage saturé');
        return false;
      }
    }
    return true;
  } catch {
    return true; // Si on ne peut pas vérifier, on laisse passer
  }
}

// ===================== OPÉRATIONS CRUD =====================

export async function getStoreData(storeName: StoreName): Promise<any[]> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return [];
  }
}

export async function getStoreItem(storeName: StoreName, id: string): Promise<any | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

export async function putStoreItem(storeName: StoreName, item: any): Promise<void> {
  try {
    const hasQuota = await checkQuota();
    if (!hasQuota) {
      console.warn(`[OfflineDB] Quota dépassé — écriture refusée pour ${storeName}/${item.id}`);
      window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
        detail: { storeName, itemId: item.id, reason: 'quota_exceeded' },
      }));
      return;
    }

    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      // Ajouter le timestamp de mise en cache
      const itemWithMeta = { ...item, _cachedAt: Date.now() };
      store.put(itemWithMeta);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error;
        if (err?.name === 'QuotaExceededError') {
          console.error(`[OfflineDB] QuotaExceededError sur ${storeName}`);
          window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
            detail: { storeName, itemId: item.id, reason: 'quota_exceeded' },
          }));
        }
        reject(err);
      };
    });
  } catch (err: any) {
    if (err?.name === 'QuotaExceededError') {
      console.error(`[OfflineDB] QuotaExceededError sur ${storeName}`);
      window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
        detail: { storeName, itemId: item.id, reason: 'quota_exceeded' },
      }));
    }
    // Pas de throw — l'UI continue de fonctionner
  }
}

export async function putStoreBulk(storeName: StoreName, items: any[]): Promise<void> {
  try {
    const hasQuota = await checkQuota();
    if (!hasQuota) {
      console.warn(`[OfflineDB] Quota dépassé — écriture bulk refusée pour ${storeName}`);
      window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
        detail: { storeName, itemCount: items.length, reason: 'quota_exceeded' },
      }));
      return;
    }

    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);

      const now = Date.now();
      for (const item of items) {
        store.put({ ...item, _cachedAt: now });
      }

      tx.oncomplete = async () => {
        db.close();
        // Mettre à jour les métadonnées de fraîcheur
        await markStoreSynced(storeName, items.length);
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error;
        if (err?.name === 'QuotaExceededError') {
          window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
            detail: { storeName, itemCount: items.length, reason: 'quota_exceeded' },
          }));
        }
        reject(err);
      };
    });
  } catch (err: any) {
    if (err?.name === 'QuotaExceededError') {
      window.dispatchEvent(new CustomEvent('offline-storage-write-failed', {
        detail: { storeName, itemCount: items.length, reason: 'quota_exceeded' },
      }));
    }
  }
}

export async function deleteStoreItem(storeName: StoreName, id: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(id);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silencieux
  }
}

export async function clearStore(storeName: StoreName): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.clear();

      tx.oncomplete = () => {
        db.close();
        // Réinitialiser les métadonnées
        updateStoreMetadata(storeName, { lastSyncedAt: 0, recordCount: 0, syncedOnline: false });
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silencieux
  }
}

export async function clearAllStores(): Promise<void> {
  for (const name of ALL_STORES) {
    await clearStore(name);
  }
  // Vider aussi les métadonnées
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('_metadata', 'readwrite');
      tx.objectStore('_metadata').clear();
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
    });
  } catch {
    // Silencieux
  }
}

// ===================== QUERY AVEC INDEX =====================

/**
 * Recherche dans un store par une propriété (ex: patient_id).
 * Utilise getAll + filtre (pas d'index pour rester simple).
 */
export async function queryStoreByField(
  storeName: StoreName,
  field: string,
  value: any,
): Promise<any[]> {
  try {
    const all = await getStoreData(storeName);
    return all.filter((item) => item[field] === value);
  } catch {
    return [];
  }
}

/**
 * Récupère les enregistrements modifiés depuis une certaine date.
 */
export async function getRecordsSince(
  storeName: StoreName,
  since: number,
): Promise<any[]> {
  try {
    const all = await getStoreData(storeName);
    return all.filter((item) => item._cachedAt && item._cachedAt >= since);
  } catch {
    return [];
  }
}